import { Trash2 } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import { Select } from './Select';
import type { SelectOption } from './selectShared';

/** One row of what has been added — the record's name, with an optional muted second line. */
export interface SearchTableInputRow {
  /** The value the search added — what `onRemove` hands back. */
  key: string;
  primary: string;
  /** A second line under the name — an employee number, an email. Dropped where it IS the name. */
  secondary?: string | null;
  /** Draw the second line in mono — a code, a number. */
  secondaryMono?: boolean;
}

/**
 * THE standard input for picking SEVERAL records whose detail matters — SEARCH, THEN A TABLE
 * (product call, 13 Oct 2026; the Share dialog's shape, cut to the record and its action).
 *
 * - **Picking ADDS a row** — the search is a `<Select>` held at `value=""`, offering only what is
 *   not already added. Nothing is written until the form around it submits.
 * - **Two columns**: the record (name over a muted second line) and ACTION.
 * - **Remove is an ICON** (`Trash2`), named by `aria-label` AND a `<Tooltip>` carrying the word —
 *   a glyph alone is a fact only a sighted reader who already knows it gets.
 * - **Never empty-looking**: with nothing added, the table says so in one line.
 *
 * The CALLER owns the list (`rows`), resolves the names, and decides what an option looks like —
 * label the name, `sublabel` the code, `keywords` the code (the picker rule: an option says what it is).
 */
export function SearchTableInput({
  id,
  columnLabel,
  searchPlaceholder,
  options,
  rows,
  onAdd,
  onRemove,
  emptyMessage,
  unavailableMessage,
  remote = false,
  loading = false,
  disabled = false,
  error,
}: {
  /** The search's DOM id — what `focusValidationError` lands on for the whole set. */
  id: string;
  /** The first column's header, singular — `Speaker`. */
  columnLabel: string;
  /** Also the search's accessible name, without the trailing ellipsis. */
  searchPlaceholder: string;
  options: SelectOption[];
  rows: SearchTableInputRow[];
  onAdd: (value: string) => void;
  onRemove: (key: string) => void;
  emptyMessage: string;
  /** Shown INSTEAD of the search when the reader cannot load the options — why, in a sentence. */
  unavailableMessage?: string | null;
  remote?: boolean;
  loading?: boolean;
  disabled?: boolean;
  error?: string;
}) {
  const taken = new Set(rows.map((row) => row.key));

  return (
    <div className="flex flex-col gap-3">
      {unavailableMessage ? (
        <p className="text-xs text-slate-500">{unavailableMessage}</p>
      ) : (
        <Select
          id={id}
          ariaLabel={searchPlaceholder.replace(/…$/, '')}
          placeholder={searchPlaceholder}
          value=""
          onChange={(value) => {
            if (value) onAdd(value);
          }}
          options={options.filter((option) => !taken.has(option.value))}
          remote={remote}
          loading={loading}
          disabled={disabled}
          error={error}
        />
      )}

      <div className="modern-scrollbar overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full table-auto">
          <thead className="bg-slate-100">
            <tr className="text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
              <th className="px-3 py-2 text-left">{columnLabel}</th>
              <th className="w-20 border-l border-slate-200 px-3 py-2 text-right">Action</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr className="border-t border-slate-200">
                <td colSpan={2} className="px-3 py-3 text-xs text-slate-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.key} className="border-t border-slate-200">
                  <td className="px-3 py-2 align-middle">
                    <p className="text-sm text-slate-600">{row.primary}</p>
                    {/* Suppressed where it IS the name — a value is never printed twice. */}
                    {row.secondary && row.secondary !== row.primary ? (
                      <p className={`text-xs text-slate-500 ${row.secondaryMono ? 'font-mono' : ''}`}>{row.secondary}</p>
                    ) : null}
                  </td>
                  <td className="border-l border-slate-200 px-3 py-2 text-right align-middle">
                    <Tooltip label="Remove">
                      <button
                        type="button"
                        aria-label={`Remove ${row.primary}`}
                        onClick={() => onRemove(row.key)}
                        disabled={disabled}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-600 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-200 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
